import { t } from "@/i18n-js/instance";
import { usePasswordField } from "@/react/hooks/usePasswordField";
import { PASSWORD_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { CheckoutForm } from "@circle-react/components/Paywalls/Checkout/CheckoutPage/CheckoutForm";
import { PasswordStrengthIndicator } from "@circle-react/components/shared/PasswordField/PasswordStrengthIndicator";
import { PasswordVisibilityToggle } from "@circle-react/components/shared/PasswordField/PasswordVisibilityToggle";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { Typography } from "@circle-react-uikit/Typography";
import { CheckoutFormField } from "../../CheckoutFormField";
import { EmailDisplayBox } from "./EmailDisplayBox";
import { SecurityBadge } from "./SecurityBadge";

interface EmailVerificationAccountSetupStateProps {
  email: string;
  onClose: () => void;
}
const LOCALE_ROOT =
  "paywall_checkout.form.email_verification_states.account_setup_state";

export const EmailVerificationAccountSetupState = ({
  email,
  onClose,
}: EmailVerificationAccountSetupStateProps) => {
  const { currentCommunity } = usePaywallCheckoutContext();
  const {
    isPasswordVisible,
    setIsPasswordVisible,
    passwordValue,
    passwordFieldError,
    passwordStrength,
    handleBlur,
  } = usePasswordField({ fieldName: "password" });

  const communityName =
    currentCommunity?.name || t([LOCALE_ROOT, "community_label"]);

  return (
    <div className="flex flex-col gap-5 pb-6 *:px-6">
      <EmailDisplayBox email={email} onClose={onClose} />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Typography.TitleSm weight="bold" size="text-base">
              {t([LOCALE_ROOT, "join_community"], {
                community_name: communityName,
              })}
            </Typography.TitleSm>
            <Typography.BodySm weight="normal" color="text-default">
              {t([LOCALE_ROOT, "create_password"], {
                community_name: communityName,
              })}
            </Typography.BodySm>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <CheckoutForm.Item name="password">
            <div className="relative flex items-center">
              <CheckoutFormField
                type={isPasswordVisible && passwordValue ? "text" : "password"}
                name="password"
                options={{
                  required: true,
                  pattern: PASSWORD_REGEX,
                }}
                className="w-full"
                checkoutFormClassName="!pr-12"
                onBlur={handleBlur}
              />
              <PasswordVisibilityToggle
                passwordValue={passwordValue}
                isPasswordVisible={isPasswordVisible}
                setIsPasswordVisible={setIsPasswordVisible}
              />
            </div>
            <PasswordStrengthIndicator
              passwordStrength={passwordStrength}
              passwordFieldError={passwordFieldError}
              passwordValue={passwordValue}
            />
          </CheckoutForm.Item>
        </div>
      </div>

      <div className="flex items-center justify-start">
        <SecurityBadge />
      </div>
    </div>
  );
};
